// Basic
import { useEffect } from 'react';
import PropTypes from 'prop-types';
// Components
import { Skeleton } from '@/common-utils/components/ui/skeleton';
// Common
import customizationFields from '../../../../fields/customizationFields';
import isOptionChecked from '../../../../utils/isOptionChecked';
// Lang
import { nsCustomization } from '@/app/i18n/namespaces';
import CheckboxHelper from '@/common-utils/components/renders/checkbox/CheckboxHelper';

/**
 * @param root0
 * @param root0.setCheckedOptions
 * @param root0.customization
 * @param root0.isLoading
 * @param root0.isDisabled
 * @param root0.checkedAdditionalServices
 * @param root0.setCheckedAdditionalServices
 * @param root0.checkedOptions
 * @returns {JSX} AdditionalServices
 */
function AdditionalServices({
    customization,
    isLoading = false,
    checkedAdditionalServices = [],
    setCheckedAdditionalServices,
    checkedOptions,
    isDisabled = false
}) {

    // Constants
    const entityPrefix = 'details.functionnalities.additional_services';
    const initialOptions = [
        customizationFields.MPF_OPTIONS.TIPS.key,
        customizationFields.MPF_OPTIONS.WAITER_NUMBER.key,
        customizationFields.MPF_OPTIONS.TABLE_NUMBER.key
    ];

    // Constants
    const isDonationModeChecked = isOptionChecked(checkedOptions, customizationFields.MPF_OPTIONS.DONATIONS_MODE.key);

    // Hooks
    // Initialize checkedAdditionalServices from customization if empty
    useEffect(() => {
        const mpfOptions = customization?.[customizationFields.MPF_OPTIONS.key];
        if (mpfOptions?.length) {
            setCheckedAdditionalServices(
                initialOptions
                    .filter((optionKey) => mpfOptions.some(({ name }) => name === optionKey))
                    .map((optionKey) =>
                        mpfOptions.find(({ name }) => name === optionKey) ?? { name: optionKey, value: null }
                    )
            );
        }
    }, [customization]);
    useEffect(() => {
        // Reset Additional Services when Donation mode is checked
        if (isDonationModeChecked) {
            setCheckedAdditionalServices([]);
        }
    }, [isDonationModeChecked]);

    // Functions
    // Handle user toggle
    const handleCheckOption = (optionKey) => {
        const alreadyChecked = isOptionChecked(checkedAdditionalServices, optionKey);

        if (alreadyChecked) {
            setCheckedAdditionalServices(checkedAdditionalServices.filter((item) => item.name !== optionKey));
        } else {
            setCheckedAdditionalServices([...checkedAdditionalServices, { name: optionKey, value: null }]);
        }
    };

    return (
        <div>
            {isLoading &&
                (
                    <div className='p-4 space-y-4'>
                        {[1, 2, 3].map((item, index) => (
                            <div key={`${index}-${item}`} className='flex items-start gap-3'>
                                <Skeleton className='h-4 w-64 bg-gray-200' />
                            </div>
                        ))}
                    </div>
                )
            }
            {!isLoading &&
                <div className='p-4 space-y-4'>
                    {initialOptions.map((optionKey, index) => (
                        <CheckboxHelper
                            key={optionKey}
                            isChecked={isOptionChecked(checkedAdditionalServices, optionKey)}
                            onCheckedChange={() => handleCheckOption(optionKey)}
                            isDisabled={isDisabled || isDonationModeChecked}
                            entity={`${entityPrefix}.option_${index + 1}`}
                            namespace={nsCustomization}
                        />
                    ))}
                </div>
            }
        </div>
    );
}

AdditionalServices.propTypes = {
    setCheckedAdditionalServices: PropTypes.func,
    checkedAdditionalServices: PropTypes.array,
    customization: PropTypes.object,
    isLoading: PropTypes.bool,
    isDisabled: PropTypes.bool,
    checkedOptions: PropTypes.array
};
export default AdditionalServices;